import { getCognitoIdTokenWithProactiveRefresh } from "@src/appV2/Auth/cognito/getCognitoIdTokenWithProactiveRefresh";
import { environmentConfig } from "@src/appV2/environment";
import axios from "axios";
import { z } from "zod";

const exchangeTokenResponseSchema = z.object({
  firebaseCustomToken: z.string(),
});

/**
 * Exchange Cognito ID token for Firebase custom token.
 * Uses backend endpoint GET /api/auth/exchange-cognito-token (PR #19595).
 *
 * Flow:
 * 1. Get current Cognito ID token
 * 2. Call backend endpoint (auth-guard validates Cognito token)
 * 3. Backend creates Firebase user if needed
 * 4. Backend returns Firebase custom token
 * 5. Return Firebase custom token for caller to use
 *
 * This function is calling the API manually because we need to ensure the
 * right token is being used. We can't use the `get()` function from
 * `@src/appV2/api` because it may get the Firebase token instead.
 */
export async function exchangeCognitoTokenForFirebase(): Promise<string> {
  const cognitoIdToken = await getCognitoIdTokenWithProactiveRefresh();
  if (!cognitoIdToken) {
    throw new Error("No Cognito ID token found");
  }

  const response = await axios.get(
    `${environmentConfig.REACT_APP_BASE_API_URL}/auth/exchange-cognito-token`,
    {
      headers: {
        Authorization: `Bearer ${cognitoIdToken}`,
        "Content-Type": "application/json",
      },
    }
  );

  // Validate response schema
  const validatedData = exchangeTokenResponseSchema.parse(response.data);
  const { firebaseCustomToken } = validatedData;

  return firebaseCustomToken;
}
